/**
 * The two axes of a timeline block, kept apart on purpose.
 *
 * ACCENT is identity — which practitioner's schedule a slot belongs to. It is
 * the only thing that picks a HUE.
 *
 * VARIANT is status — booked, delivered, tentative, not performed. It never
 * picks a hue; it selects weights and edges within whatever accent it is given.
 * Before this split the calendar coloured by status and the plan timeline
 * coloured by weight, so the same fact read two ways and a practitioner's blue
 * was indistinguishable from "booked".
 */

export type TimelineAccent =
  | "primary"
  | "muted"
  | "blue"
  | "emerald"
  | "violet"
  | "orange"
  | "pink"
  | "cyan"
  | "rose"
  | "teal"
  | "slate"
  | "red"
  | "amber"
  | "inverted";

interface Accent {
  /**
   * Three weights, because two could not serve both surfaces: the calendar
   * needs a BOOKED slot to read as present and solid, while the plan timeline
   * needs delivered to outweigh scheduled. `fill` is the everyday weight, and
   * the other two step either side of it.
   */
  fill: string;
  fillStrong: string;
  fillSoft: string;
  /**
   * The block's border colour, applied to ALL FOUR SIDES. Width does the rest:
   * 2px around, 3px on the left rule. One colour everywhere is what makes the
   * corner work — a 3px rule mitering into a *contrasting* 1px edge flared a
   * visible wedge, while a 1px width step in the same colour is invisible.
   *
   * It must be a BORDER, not a ring: the calendar draws each event inside an
   * `overflow: hidden` button, which clips an outset ring away completely, so
   * calendar tiles lost their border while list rows kept theirs.
   */
  line: string;
  lineSoft: string;
  /** The left rule, at full strength while `line` sits behind it at 50%. */
  rule: string;
  ruleSoft: string;
  text: string;
  subtext: string;
  /** The filter bar's swatch. */
  dot: string;
}

export const ACCENTS: Record<TimelineAccent, Accent> = {
  // The product accent, and the default everywhere outside the calendar: a plan
  // has no practitioner to distinguish.
  primary: {
    fill: "bg-primary/5",
    fillStrong: "bg-primary/10",
    fillSoft: "bg-primary/[0.03]",
    line: "border-primary/50",
    lineSoft: "border-primary/20",
    rule: "border-l-primary",
    ruleSoft: "border-l-primary/40",
    text: "text-foreground",
    subtext: "text-muted-foreground",
    dot: "bg-primary",
  },
  muted: {
    fill: "bg-muted/30",
    fillStrong: "bg-muted/40",
    fillSoft: "bg-muted/20",
    line: "border-muted-foreground/50",
    lineSoft: "border-muted-foreground/25",
    rule: "border-l-muted-foreground",
    ruleSoft: "border-l-muted-foreground/50",
    text: "text-muted-foreground",
    subtext: "text-muted-foreground",
    dot: "bg-muted-foreground",
  },
  blue: {
    fill: "bg-blue-100 dark:bg-blue-800",
    fillStrong: "bg-blue-200 dark:bg-blue-700",
    fillSoft: "bg-blue-50 dark:bg-blue-900",
    line: "border-blue-500/50",
    lineSoft: "border-blue-400/50",
    rule: "border-l-blue-500",
    ruleSoft: "border-l-blue-400",
    text: "text-blue-900 dark:text-blue-50",
    subtext: "text-blue-700 dark:text-blue-200",
    dot: "bg-blue-500",
  },
  emerald: {
    fill: "bg-emerald-100 dark:bg-emerald-800",
    fillStrong: "bg-emerald-200 dark:bg-emerald-700",
    fillSoft: "bg-emerald-50 dark:bg-emerald-900",
    line: "border-emerald-500/50",
    lineSoft: "border-emerald-400/50",
    rule: "border-l-emerald-500",
    ruleSoft: "border-l-emerald-400",
    text: "text-emerald-900 dark:text-emerald-50",
    subtext: "text-emerald-700 dark:text-emerald-200",
    dot: "bg-emerald-500",
  },
  violet: {
    fill: "bg-violet-100 dark:bg-violet-800",
    fillStrong: "bg-violet-200 dark:bg-violet-700",
    fillSoft: "bg-violet-50 dark:bg-violet-900",
    line: "border-violet-500/50",
    lineSoft: "border-violet-400/50",
    rule: "border-l-violet-500",
    ruleSoft: "border-l-violet-400",
    text: "text-violet-900 dark:text-violet-50",
    subtext: "text-violet-700 dark:text-violet-200",
    dot: "bg-violet-500",
  },
  orange: {
    fill: "bg-orange-100 dark:bg-orange-800",
    fillStrong: "bg-orange-200 dark:bg-orange-700",
    fillSoft: "bg-orange-50 dark:bg-orange-900",
    line: "border-orange-500/50",
    lineSoft: "border-orange-400/50",
    rule: "border-l-orange-500",
    ruleSoft: "border-l-orange-400",
    text: "text-orange-900 dark:text-orange-50",
    subtext: "text-orange-700 dark:text-orange-200",
    dot: "bg-orange-500",
  },
  pink: {
    fill: "bg-pink-100 dark:bg-pink-800",
    fillStrong: "bg-pink-200 dark:bg-pink-700",
    fillSoft: "bg-pink-50 dark:bg-pink-900",
    line: "border-pink-500/50",
    lineSoft: "border-pink-400/50",
    rule: "border-l-pink-500",
    ruleSoft: "border-l-pink-400",
    text: "text-pink-900 dark:text-pink-50",
    subtext: "text-pink-700 dark:text-pink-200",
    dot: "bg-pink-500",
  },
  cyan: {
    fill: "bg-cyan-100 dark:bg-cyan-800",
    fillStrong: "bg-cyan-200 dark:bg-cyan-700",
    fillSoft: "bg-cyan-50 dark:bg-cyan-900",
    line: "border-cyan-500/50",
    lineSoft: "border-cyan-400/50",
    rule: "border-l-cyan-500",
    ruleSoft: "border-l-cyan-400",
    text: "text-cyan-900 dark:text-cyan-50",
    subtext: "text-cyan-700 dark:text-cyan-200",
    dot: "bg-cyan-500",
  },
  rose: {
    fill: "bg-rose-100 dark:bg-rose-800",
    fillStrong: "bg-rose-200 dark:bg-rose-700",
    fillSoft: "bg-rose-50 dark:bg-rose-900",
    line: "border-rose-500/50",
    lineSoft: "border-rose-400/50",
    rule: "border-l-rose-500",
    ruleSoft: "border-l-rose-400",
    text: "text-rose-900 dark:text-rose-50",
    subtext: "text-rose-700 dark:text-rose-200",
    dot: "bg-rose-500",
  },
  teal: {
    fill: "bg-teal-100 dark:bg-teal-800",
    fillStrong: "bg-teal-200 dark:bg-teal-700",
    fillSoft: "bg-teal-50 dark:bg-teal-900",
    line: "border-teal-500/50",
    lineSoft: "border-teal-400/50",
    rule: "border-l-teal-500",
    ruleSoft: "border-l-teal-400",
    text: "text-teal-900 dark:text-teal-50",
    subtext: "text-teal-700 dark:text-teal-200",
    dot: "bg-teal-500",
  },
  // Context that belongs to someone else — an existing booking shown only so you
  // can place around it. Recedes rather than identifies.
  slate: {
    fill: "bg-slate-100/80 dark:bg-slate-800/50",
    fillStrong: "bg-slate-200/80 dark:bg-slate-700/50",
    fillSoft: "bg-slate-50/80 dark:bg-slate-900/50",
    line: "border-slate-400/50",
    lineSoft: "border-slate-300/50",
    rule: "border-l-slate-400",
    ruleSoft: "border-l-slate-300",
    text: "text-foreground",
    subtext: "text-muted-foreground",
    dot: "bg-slate-400",
  },
  // The editing surface's "this is the step you have hold of": a solid fill
  // rather than a tint, so it cannot be confused with any weight of any other
  // accent. Never in the rotation — it says selected, not who.
  inverted: {
    fill: "bg-primary",
    fillStrong: "bg-primary",
    fillSoft: "bg-primary",
    line: "border-primary",
    lineSoft: "border-primary",
    rule: "border-l-primary",
    ruleSoft: "border-l-primary",
    text: "text-primary-foreground",
    subtext: "text-primary-foreground/80",
    dot: "bg-primary",
  },
  // Work a booking proposal would add, not yet submitted. Never in the rotation,
  // so it cannot be mistaken for a practitioner.
  amber: {
    fill: "bg-amber-50 dark:bg-amber-900",
    fillStrong: "bg-amber-100 dark:bg-amber-800",
    fillSoft: "bg-amber-50/60 dark:bg-amber-950",
    line: "border-amber-500/50",
    lineSoft: "border-amber-400/50",
    rule: "border-l-amber-500",
    ruleSoft: "border-l-amber-400",
    text: "text-foreground",
    subtext: "text-amber-800 dark:text-amber-200",
    dot: "bg-amber-500",
  },
  red: {
    fill: "bg-red-100 dark:bg-red-800",
    fillStrong: "bg-red-200 dark:bg-red-700",
    fillSoft: "bg-red-50 dark:bg-red-900",
    line: "border-red-500/50",
    lineSoft: "border-red-400/50",
    rule: "border-l-red-500",
    ruleSoft: "border-l-red-400",
    text: "text-red-900 dark:text-red-50",
    subtext: "text-red-700 dark:text-red-200",
    dot: "bg-red-500",
  },
};

/**
 * The rotation a schedule's accent is drawn from, in order. `primary` and
 * `muted` are excluded: they carry meaning of their own (product default, and
 * work that did not happen) and must not land on a practitioner.
 */
export const SCHEDULE_ACCENTS: TimelineAccent[] = [
  "blue",
  "emerald",
  "violet",
  "orange",
  "pink",
  "cyan",
  "rose",
  "teal",
];

export type TimelineVariant =
  | "delivered"
  | "inProgress"
  | "unableToComplete"
  | "scheduled"
  | "tentative"
  | "unscheduled"
  | "notPerformed"
  | "emphasis"
  | "proposed"
  | "selected";

interface Variant {
  weight: "strong" | "base" | "soft";
  dashed?: boolean;
  /** Overrides the accent: work that did not happen carries no identity. */
  accent?: TimelineAccent;
  extra?: string;
}

// Delivered and scheduled differ by WEIGHT, not by hue: a slot that ran is the
// same work as a slot that is booked, and splitting them across two hues read as
// two unrelated kinds of thing.
export const VARIANTS: Record<TimelineVariant, Variant> = {
  delivered: { weight: "strong" },
  inProgress: { weight: "base", accent: "blue" },
  unableToComplete: { weight: "base", accent: "amber", dashed: true },
  // Booked work is PRESENT, so it takes the everyday weight, not a faint one.
  // A pale fill left the 3px rule reading as a stick floating beside nothing.
  scheduled: { weight: "base" },
  tentative: { weight: "soft", dashed: true },
  unscheduled: { weight: "soft", accent: "muted" },
  notPerformed: { weight: "soft", accent: "muted", extra: "line-through" },
  // Marks a procedure another item depends on. A ring rather than a hue, so it
  // composes with whichever accent the block already carries.
  // Selection is UI state, not status, but it marks the block the same way the
  // others do — so it rides the same table and overrides the accent, exactly as
  // notPerformed does.
  selected: { weight: "strong", accent: "inverted" },
  // Unsaved, like selection, so it overrides the accent: the editor shows one
  // visit, where no practitioner hue is in play.
  proposed: { weight: "base", accent: "amber" },
  emphasis: { weight: "strong", extra: "ring-1 ring-inset ring-current/30" },
};

/** Appointment status → the variant that expresses it. Hue is never involved. */
export function visitVariant(status: string | undefined): TimelineVariant {
  if (status === "completed") return "delivered";
  if (status === "cancelled" || status === "noShow") return "notPerformed";
  if (status === "proposed") return "tentative";
  return "scheduled";
}
